<template>
	<div id="app">
		<login v-if="!authorized" :session-ended="sessionEnded" @loginAttempt="checkLogin"></login>
		<el-container v-else>
			<el-aside width="232px" class="side-nav">
				<img src="./images/logo-white.svg" alt="Lavish Locations" class="lavish-logo"/>
				<el-button class="create-location-button" type="primary" icon="el-icon-plus"
				           @click="openNewLocationPrompt" round>New Location
				</el-button>

				<!-- Each item's index is its route, so the current page is highlighted. -->
				<el-menu class="side-nav__menu" :router="true" :default-active="activeMenu">
					<el-menu-item index="/">
						<i class="el-icon-s-home"></i>
						<span>Home</span>
					</el-menu-item>

					<el-submenu index="locations">
						<template slot="title">
							<i class="el-icon-house"></i>
							<span>Locations</span>
						</template>
						<el-menu-item index="/pending-locations">Pending locations</el-menu-item>
					</el-submenu>

					<el-menu-item index="/attributes">
						<i class="el-icon-price-tag"></i>
						<span>Tags/Attributes</span>
					</el-menu-item>

					<el-submenu index="portfolios">
						<template slot="title">
							<i class="el-icon-document"></i>
							<span>Portfolios</span>
						</template>
						<el-menu-item index="/deletedPortfolios">Deleted</el-menu-item>
					</el-submenu>

					<el-submenu index="website-content">
						<template slot="title">
							<i class="el-icon-edit"></i>
							<span>Website Content</span>
						</template>
						<el-menu-item-group title="Pages">
							<el-menu-item index="/homepage">Home Page</el-menu-item>
							<el-menu-item index="/about">Ask an Expert</el-menu-item>
							<el-menu-item index="/information">Guide</el-menu-item>
							<el-menu-item index="/careers">Careers</el-menu-item>
						</el-menu-item-group>
						<el-menu-item-group title="Legal">
							<el-menu-item index="/cookiepolicy">Cookie Policy</el-menu-item>
							<el-menu-item index="/privacypolicy">Privacy Policy</el-menu-item>
							<el-menu-item index="/terms">Terms & Conditions</el-menu-item>
						</el-menu-item-group>
						<el-menu-item-group title="Media">
							<el-menu-item index="/posters">Posters</el-menu-item>
							<el-menu-item index="/collections">Collections</el-menu-item>
							<el-menu-item index="/bannerImages">Banner Images</el-menu-item>
						</el-menu-item-group>
					</el-submenu>
				</el-menu>

				<p class="cms-version" :title="'Commit ' + commit">
					v{{ version }}<span v-if="isDev"> (dev)</span>
				</p>
			</el-aside>

			<el-main>
				<el-header class="top-header" height="60px">
					<el-autocomplete
						prefix-icon="el-icon-search"
						class="location-search"
						v-model="state"
						:fetch-suggestions="querySearch"
						placeholder="Search a Location Ref..."
						popper-class="location-search__popper"
						@select="handleSelect"
					>
						<template #default="{ item }">
							<div class="searched-item">
								<div class="searched-item__ref">{{ item.locationReference }}</div>
								<div class="searched-item__name">{{ item.locationName }}</div>
								<div>{{ [item.addressOne, item.postcode].filter(Boolean).join(', ') }}</div>
								<div>{{ item.firstName }} {{ item.lastName }}</div>
							</div>
						</template>
					</el-autocomplete>

					<el-button class="logout-button" type="text" icon="el-icon-switch-button" @click="logout">
						Logout
					</el-button>
				</el-header>

				<div class="router-container">
					<router-view></router-view>
				</div>
			</el-main>
		</el-container>
	</div>
</template>

<script>
import newLocation from './mixins/newLocation';

export default {
	mixins: [newLocation],
	data() {
		return {
			authorized: false,
			// A saved sign-in was refused (it ran out or was revoked), so the login screen says so.
			sessionEnded: false,
			// Set at build time in webpack.config.js, so you can tell which release is live.
			version: process.env.VUE_APP_VERSION,
			commit: process.env.VUE_APP_COMMIT,
			isDev: process.env.VUE_APP_NODE_ENV === 'development',

			state: ''
		}
	},
	computed: {
		// The sidebar item to highlight. A location's own screen highlights nothing.
		activeMenu() {
			return this.$route.path;
		},
	},
	methods: {
		querySearch(queryString, cb) {

			if (queryString.length > 0) {
				this.axios.get(`${process.env.VUE_APP_ROOT_API}/api/locations/cmssearch/${queryString}`, {
					headers: {
						Authorization: localStorage.getItem('Authorization')
					}
				})
					.then(response => {

						let results = response.data;
						for (let i = 0; i < results.length; i++) {
							results[i].link = results[i].locationReference;
							results[i].value = results[i].locationReference;
						}
						cb(results);

					})
					.catch(error => {


					});
			}


		},

		handleSelect(item) {
			this.$router.push('/locations/' + item.locationReference);
			this.$router.go();
		},


		checkLogin: function (response) {
			if (response.token) {
				localStorage.setItem('Authorization', `Bearer ${response.token}`);
				this.sessionEnded = false;
				this.authorized = true;

			} else {
				this.$notify.error({
					title: 'Error',
					message: 'Incorrect username or password'
				});
				console.log('fail');
			}
		},

		// Admin sign-ins last 7 days of not using the CMS (the website's utilityData/session.js), so
		// swap the token for a fresh one once it's a day old: on load and every 6 hours while open.
		renewSession() {
			const header = localStorage.getItem('Authorization') || '';
			let issuedAt = 0;
			try {
				issuedAt = JSON.parse(atob(header.split(' ')[1].split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).iat;
			} catch (e) {
				return;
			}
			if (Date.now() / 1000 - issuedAt < 24 * 60 * 60) return;
			this.axios.post(`${process.env.VUE_APP_ROOT_API}/api/login/refresh`, {}, {headers: {Authorization: header}})
				.then(response => {
					if (response.data && response.data.token) {
						localStorage.setItem('Authorization', `Bearer ${response.data.token}`);
					}
				})
				.catch(() => {
				});
		},

		// Removes the header as well as the local value.
		logout: function () {
			localStorage.removeItem('Authorization');
			this.authorized = false;
		}
	},

	// So that when page is refreshed, they are still logged in. This is mainly used for the location page because it refreshes the page after grabbing the URL param.
	// This needs to be fixed so it doesn't look like the page reloads.
	mounted() {
		this.renewTimer = setInterval(() => {
			if (this.authorized) this.renewSession();
		}, 6 * 60 * 60 * 1000);
	},

	beforeDestroy() {
		clearInterval(this.renewTimer);
	},

	beforeMount() {

		if (localStorage.Authorization) {

			// Just because auth header exists doesnt mean its still valid. Check its valid, if not then logout.
			this.axios.get(`${process.env.VUE_APP_ROOT_API}/api/login/checkAuth`, {
				headers: {
					Authorization: localStorage.getItem('Authorization')
				}
			})
				.then(response => {
					this.authorized = true;
					this.renewSession();
				})
				.catch(error => {

					this.sessionEnded = true;
					this.logout();

				});


		}
	},

}
</script>

<style>

@import './style/element-theme.css';
@import './style/cms.css';

/* ---- Sidebar ---- */

.side-nav {
	min-height: 100vh;
	padding-bottom: 16px;
	background-color: var(--lav-gray);
}

.lavish-logo {
	display: block;
	height: 2.6rem;
	margin: 24px auto 20px;
}

.create-location-button {
	display: block;
	margin: 0 auto 16px;
}

.side-nav__menu,
.side-nav__menu .el-menu {
	background-color: transparent;
	border-right: 0 !important;
}

.side-nav__menu > .el-menu-item,
.side-nav__menu > .el-submenu {
	margin: 2px 12px;
}

.side-nav__menu .el-menu-item,
.side-nav__menu .el-submenu__title {
	height: 44px;
	line-height: 44px;
	padding: 0 12px !important;
	border-radius: 8px;
	color: rgba(255, 255, 255, 0.88);
	font-size: 14px;
}

.side-nav__menu .el-submenu .el-menu-item {
	min-width: 0;
	height: 38px;
	line-height: 38px;
	padding-left: 40px !important;
}

.side-nav__menu .el-menu-item i,
.side-nav__menu .el-submenu__title i {
	color: var(--lav-green);
}

.side-nav__menu .el-submenu__title i.el-submenu__icon-arrow {
	color: rgba(255, 255, 255, 0.5);
}

.side-nav__menu .el-menu-item:hover,
.side-nav__menu .el-menu-item:focus,
.side-nav__menu .el-submenu__title:hover {
	background-color: var(--lav-gray-dark);
}

/* The page you're on. */
.side-nav__menu .el-menu-item.is-active {
	background-color: rgba(50, 204, 153, 0.18);
	color: #fff;
	font-weight: 600;
}

/* Group headings inside "Website Content" (Pages / Legal / Media). */
.side-nav .el-menu-item-group__title {
	padding: 12px 0 4px 40px !important;
	color: rgba(255, 255, 255, 0.5);
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
}

.cms-version {
	padding: 16px 24px;
	font-size: 12px;
	color: rgba(255, 255, 255, 0.6);
}

/* ---- Header ---- */

.el-main {
	padding: 0;
	background: var(--page-bg);
}

.top-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: white;
	border-bottom: 1px solid var(--border);
}

.location-search {
	width: 320px;
}

.location-search .el-input {
	width: 100%;
}

.logout-button {
	color: var(--text-muted);
}

.logout-button:hover,
.logout-button:focus {
	color: var(--lav-green-dark);
}

.searched-item {
	padding: 8px 0;
	line-height: 18px;
	color: var(--text-muted);
	font-size: 13px;
}

.searched-item__ref {
	color: var(--lav-green-dark);
	font-weight: 600;
}

.searched-item__name {
	color: var(--text);
}

.location-search__popper {
	min-width: 320px;
}

.router-container {
	padding: 16px;
}

/* Older screens' white card. New screens use the content-layout component instead. */
.white-container {
	border: 1px solid var(--border);
	border-radius: var(--radius);
	background-color: white;
	padding: 24px 32px;
	min-height: calc(100vh - 92px);
	box-sizing: border-box;
}

</style>



// WEBPACK FOOTER //
// src/App.vue